(() => {
  if (window.AFGuidedCollection) return;

  const sectionSelector = '[data-section-type="af-guided-collection-landing"]';
  const sectionCleanups = new WeakMap();

  const prefersReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => prefersReducedMotion() ? 'auto' : 'smooth';

  const addCleanup = (section, cleanup) => {
    const cleanups = sectionCleanups.get(section) || [];
    cleanups.push(cleanup);
    sectionCleanups.set(section, cleanups);
  };

  const addListener = (section, element, eventName, handler, options) => {
    if (!element) return;
    element.addEventListener(eventName, handler, options);
    addCleanup(section, () => element.removeEventListener(eventName, handler, options));
  };

  const teardownSection = (section) => {
    const cleanups = sectionCleanups.get(section) || [];
    cleanups.forEach((cleanup) => cleanup());
    sectionCleanups.delete(section);

    delete section.dataset.afProcessCarouselInit;
    delete section.dataset.afFeaturedCarouselInit;
    delete section.dataset.afRoutinesCarouselInit;
    delete section.dataset.afRoutineIncludesInit;
    delete section.dataset.afGuidesCarouselInit;
    delete section.dataset.afFinalCarouselInit;
    delete section.dataset.afStickyCtaInit;
  };

  const initScrollCarousel = (section, options) => {
    if (section.dataset[options.initKey] === 'true') return;

    const rail = section.querySelector(options.railSelector);
    const prevButton = section.querySelector(options.prevSelector);
    const nextButton = section.querySelector(options.nextSelector);
    if (!rail || !prevButton || !nextButton) return;

    section.dataset[options.initKey] = 'true';

    const scrollStep = () => Math.round(rail.clientWidth * 0.9);
    const updateActiveItem = () => {
      if (!options.activeSelector) return;

      const items = Array.from(rail.querySelectorAll(options.activeSelector));
      if (!items.length) return;

      const railCenter = rail.scrollLeft + (rail.clientWidth / 2);
      const activeItem = items.reduce((closestItem, item) => {
        const closestCenter = closestItem.offsetLeft + (closestItem.offsetWidth / 2);
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        return Math.abs(itemCenter - railCenter) < Math.abs(closestCenter - railCenter) ? item : closestItem;
      }, items[0]);

      items.forEach((item) => {
        const isActive = item === activeItem;
        item.classList.toggle(options.activeClass || 'is-active', isActive);
        if (item.tagName === 'A') {
          if (isActive) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        }
      });
    };

    const updateButtons = () => {
      const maxScrollLeft = rail.scrollWidth - rail.clientWidth - 2;
      const canScroll = rail.scrollWidth > rail.clientWidth + 4;

      if (options.clampScroll && rail.scrollLeft > maxScrollLeft + 2) {
        rail.scrollTo({ left: Math.max(maxScrollLeft, 0), behavior: 'auto' });
      }

      prevButton.hidden = !canScroll;
      nextButton.hidden = !canScroll;
      prevButton.disabled = rail.scrollLeft <= 2;
      nextButton.disabled = rail.scrollLeft >= maxScrollLeft;
      updateActiveItem();
    };

    addListener(section, prevButton, 'click', () => {
      rail.scrollBy({ left: -scrollStep(), behavior: scrollBehavior() });
    });

    addListener(section, nextButton, 'click', () => {
      rail.scrollBy({ left: scrollStep(), behavior: scrollBehavior() });
    });

    addListener(section, rail, 'scroll', updateButtons, { passive: true });
    addListener(section, window, 'resize', updateButtons);

    if (options.observeResize && 'ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateButtons);
      resizeObserver.observe(rail);
      if (rail.parentElement) resizeObserver.observe(rail.parentElement);
      addCleanup(section, () => resizeObserver.disconnect());
    }

    updateButtons();
  };

  const initProcessCarousel = (section) => {
    if (section.dataset.afProcessCarouselInit === 'true') return;

    const rail = section.querySelector('.af-gc__process');
    const prevButton = section.querySelector('.af-gc__process-nav-button--prev');
    const nextButton = section.querySelector('.af-gc__process-nav-button--next');
    if (!rail || !prevButton || !nextButton) return;

    section.dataset.afProcessCarouselInit = 'true';

    const stages = Array.from(rail.querySelectorAll('.af-gc__stage'));
    const stageScrollLeft = (stage) => Math.round(stage.offsetLeft - ((rail.clientWidth - stage.offsetWidth) / 2));
    const currentStageIndex = () => {
      const currentScrollLeft = rail.scrollLeft;
      return stages.reduce((closestIndex, stage, index) => {
        const closestDistance = Math.abs(stageScrollLeft(stages[closestIndex]) - currentScrollLeft);
        const stageDistance = Math.abs(stageScrollLeft(stage) - currentScrollLeft);
        return stageDistance < closestDistance ? index : closestIndex;
      }, 0);
    };
    const scrollToStage = (direction) => {
      if (!stages.length) return;
      const targetIndex = Math.min(Math.max(currentStageIndex() + direction, 0), stages.length - 1);
      rail.scrollTo({ left: stageScrollLeft(stages[targetIndex]), behavior: scrollBehavior() });
    };

    const updateButtons = () => {
      const maxScrollLeft = rail.scrollWidth - rail.clientWidth - 2;
      const canScroll = rail.scrollWidth > rail.clientWidth + 4;
      const activeIndex = stages.length ? currentStageIndex() : 0;

      prevButton.hidden = !canScroll;
      nextButton.hidden = !canScroll;
      prevButton.disabled = rail.scrollLeft <= 2 || activeIndex <= 0;
      nextButton.disabled = rail.scrollLeft >= maxScrollLeft || activeIndex >= stages.length - 1;
    };

    addListener(section, prevButton, 'click', () => scrollToStage(-1));
    addListener(section, nextButton, 'click', () => scrollToStage(1));
    addListener(section, rail, 'scroll', updateButtons, { passive: true });
    addListener(section, window, 'resize', updateButtons);

    updateButtons();
  };

  const initRoutineIncludes = (section) => {
    if (section.dataset.afRoutineIncludesInit === 'true') return;

    const toggles = section.querySelectorAll('[data-af-gc-routine-toggle]');
    if (!toggles.length) return;

    section.dataset.afRoutineIncludesInit = 'true';

    toggles.forEach((toggle) => {
      const panelId = toggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel || !section.contains(panel)) return;

      const icon = toggle.querySelector('.af-gc__routine-includes-toggle-icon');
      const updateOverflow = () => {
        const hasOverflow = !panel.hidden && panel.scrollHeight > panel.clientHeight + 2;
        panel.dataset.afGcOverflows = String(hasOverflow);
      };

      addListener(section, toggle, 'click', () => {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isExpanded));
        panel.hidden = isExpanded;
        if (icon) icon.textContent = isExpanded ? '+' : '\u2212';
        updateOverflow();
      });

      addListener(section, window, 'resize', updateOverflow);
    });
  };

  const initStickyCta = (section) => {
    if (section.dataset.afStickyCtaInit === 'true') return;

    const stickyCta = section.querySelector('[data-af-gc-sticky-cta]');
    const hero = section.querySelector('.af-gc__hero');
    const finalCta = section.querySelector('.af-gc__final');
    if (!stickyCta || !hero || !('IntersectionObserver' in window)) return;

    section.dataset.afStickyCtaInit = 'true';

    const collectionGrid = document.querySelector('[data-collection-products]') || document.querySelector('[data-section-type="collection-template"] .collection__products') || document.querySelector('[data-section-type="collection-template"]');
    const reduceMotion = prefersReducedMotion();
    let isPastHero = false;
    let hasReachedCollection = false;
    let hasReachedFinalCta = false;
    let hideTimer = null;

    const setVisibility = () => {
      const shouldShow = isPastHero && !hasReachedCollection && !hasReachedFinalCta;
      window.clearTimeout(hideTimer);

      if (shouldShow) {
        stickyCta.hidden = false;
        stickyCta.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => stickyCta.classList.add('is-visible'));
        return;
      }

      stickyCta.classList.remove('is-visible');
      stickyCta.setAttribute('aria-hidden', 'true');
      hideTimer = window.setTimeout(() => {
        if (!stickyCta.classList.contains('is-visible')) stickyCta.hidden = true;
      }, reduceMotion ? 0 : 200);
    };

    const heroObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      isPastHero = entry.boundingClientRect.bottom <= 0 && !entry.isIntersecting;
      setVisibility();
    }, { threshold: 0 });

    heroObserver.observe(hero);
    addCleanup(section, () => heroObserver.disconnect());
    addCleanup(section, () => window.clearTimeout(hideTimer));

    if (collectionGrid) {
      const collectionObserver = new IntersectionObserver((entries) => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        hasReachedCollection = entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top <= viewportHeight);
        setVisibility();
      }, { threshold: 0 });

      collectionObserver.observe(collectionGrid);
      addCleanup(section, () => collectionObserver.disconnect());
    }

    if (finalCta) {
      const finalCtaObserver = new IntersectionObserver((entries) => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        hasReachedFinalCta = entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top <= viewportHeight);
        setVisibility();
      }, { threshold: 0 });

      finalCtaObserver.observe(finalCta);
      addCleanup(section, () => finalCtaObserver.disconnect());
    }
  };

  const initSection = (section) => {
    if (!section || !section.matches(sectionSelector)) return;

    initProcessCarousel(section);
    initScrollCarousel(section, {
      initKey: 'afFeaturedCarouselInit',
      railSelector: '[data-af-gc-featured-rail]',
      prevSelector: '[data-af-gc-featured-prev]',
      nextSelector: '[data-af-gc-featured-next]'
    });
    initScrollCarousel(section, {
      initKey: 'afRoutinesCarouselInit',
      railSelector: '[data-af-gc-routines-rail]',
      prevSelector: '[data-af-gc-routines-prev]',
      nextSelector: '[data-af-gc-routines-next]'
    });
    initRoutineIncludes(section);
    initScrollCarousel(section, {
      initKey: 'afGuidesCarouselInit',
      railSelector: '[data-af-gc-guides-rail]',
      prevSelector: '[data-af-gc-guides-prev]',
      nextSelector: '[data-af-gc-guides-next]'
    });
    initScrollCarousel(section, {
      initKey: 'afFinalCarouselInit',
      railSelector: '[data-af-gc-final-rail]',
      prevSelector: '[data-af-gc-final-prev]',
      nextSelector: '[data-af-gc-final-next]',
      activeSelector: '.af-gc__final-chip',
      activeClass: 'is-active',
      clampScroll: true,
      observeResize: true
    });
    initStickyCta(section);
  };

  const initAll = (root = document) => {
    const sections = root.matches && root.matches(sectionSelector) ? [root] : Array.from(root.querySelectorAll(sectionSelector));
    sections.forEach(initSection);
  };

  window.AFGuidedCollection = {
    init: initAll,
    unload: teardownSection
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(`${sectionSelector}[data-section-id="${event.detail.sectionId}"]`) || event.target.closest(sectionSelector);
    if (section) teardownSection(section);
  });
})();